import React, { useMemo } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { BarChart3, PieChart, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import { Bet, BetStats } from "@/api/betting";
import { Progress } from "@/components/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface BettingStatsProps {
  bets: Bet[];
  personas: {
    persona_id: string;
    name: string;
    [key: string]: unknown;
  }[];
  statsData?: BetStats;
}

// Individual Stat Card Component
const StatCard = ({
  title,
  value,
  description,
  className,
  icon,
  trend,
}: {
  title: string;
  value: string | number;
  description?: string;
  className?: string;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    direction: "up" | "down" | "neutral";
  };
}) => (
  <Card
    className={cn(
      "bg-black/60 backdrop-blur-sm relative overflow-hidden",
      className,
    )}
  >
    <CardHeader className="p-3 pb-1">
      <div className="flex justify-between items-center">
        <CardTitle className="text-sm font-medium text-zinc-400">
          {title}
        </CardTitle>
        {icon}
      </div>
      <div className="text-xl font-bold mt-1">{value}</div>
      {description && (
        <div className="text-xs text-muted-foreground mt-1">{description}</div>
      )}
    </CardHeader>
    {trend && (
      <CardContent className="p-3 pt-0">
        <div className="flex items-center gap-1 text-xs">
          <span
            className={cn(
              "font-medium",
              trend.direction === "up"
                ? "text-green-500"
                : trend.direction === "down"
                  ? "text-red-500"
                  : "text-gray-500",
            )}
          >
            {trend.direction === "up" && "↑"}
            {trend.direction === "down" && "↓"}
            {trend.value.toFixed(1)}%
          </span>
          <span className="text-zinc-500">since last month</span>
        </div>
      </CardContent>
    )}
  </Card>
);

// Win Rate Card Component with Visualization
const WinRateCard = ({
  wonBets,
  lostBets,
  winPercentage,
}: {
  wonBets: number;
  lostBets: number;
  winPercentage: number;
}) => (
  <Card className="bg-black/60 backdrop-blur-sm">
    <CardHeader className="p-3 pb-2">
      <div className="flex justify-between items-center">
        <CardTitle className="text-sm font-medium text-zinc-400">
          Win Rate
        </CardTitle>
        <BarChart3 className="h-4 w-4 text-zinc-400" />
      </div>
      <div className="text-xl font-bold mt-1">{winPercentage.toFixed(1)}%</div>
      <div className="text-xs text-muted-foreground mt-1">
        {wonBets} won, {lostBets} lost
      </div>
    </CardHeader>
    <CardContent className="p-3 pt-0">
      <div className="w-full h-6 rounded-md bg-black/30 flex overflow-hidden">
        <div
          className="bg-green-600 h-full flex items-center justify-center"
          style={{ width: `${winPercentage}%` }}
        >
          {winPercentage >= 20 && (
            <span className="text-xs text-white font-medium px-1">
              {Math.round(winPercentage)}%
            </span>
          )}
        </div>
        <div
          className="bg-red-600 h-full flex items-center justify-center"
          style={{ width: `${100 - winPercentage}%` }}
        >
          {100 - winPercentage >= 20 && (
            <span className="text-xs text-white font-medium px-1">
              {Math.round(100 - winPercentage)}%
            </span>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

// Top Agents Component with Performance Indicators
const TopAgentsCard = ({
  agents,
}: {
  agents: Array<{
    persona_id: string;
    name: string;
    winPercentage: number;
    totalBets: number;
  }>;
}) => (
  <Card className="bg-black/60 backdrop-blur-sm">
    <CardHeader className="p-3">
      <div className="flex justify-between items-center">
        <CardTitle className="text-sm font-medium text-zinc-400">
          Top Performing Agents
        </CardTitle>
        <PieChart className="h-4 w-4 text-zinc-400" />
      </div>
    </CardHeader>
    <CardContent className="p-3 pt-0">
      <div className="space-y-3">
        {agents.length > 0 ? (
          agents.map((agent, index) => (
            <div key={agent.persona_id}>
              <div className="flex justify-between items-center mb-1">
                <div className="flex items-center gap-1">
                  <span className="text-zinc-500 text-xs font-medium">
                    {index + 1}.
                  </span>
                  <span className="text-sm font-medium truncate max-w-32">
                    {agent.name}
                  </span>
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1">
                        <span
                          className={cn(
                            "text-xs font-medium",
                            agent.winPercentage >= 60
                              ? "text-green-500"
                              : agent.winPercentage <= 40
                                ? "text-red-500"
                                : "text-zinc-400",
                          )}
                        >
                          {agent.winPercentage.toFixed(1)}%
                        </span>
                        <span className="text-xs text-zinc-600">
                          {agent.totalBets}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">{agent.totalBets} total bets</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Progress
                value={agent.winPercentage}
                className="h-1 bg-zinc-800"
              />
            </div>
          ))
        ) : (
          <div className="text-center text-zinc-500 text-xs py-4">
            No data available
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

// Sports Distribution Card
const SportsDistributionCard = ({
  sports,
}: {
  sports: Array<{
    name: string;
    winRate: number;
    count: number;
  }>;
}) => (
  <Card className="bg-black/60 backdrop-blur-sm">
    <CardHeader className="p-3">
      <div className="flex justify-between items-center">
        <CardTitle className="text-sm font-medium text-zinc-400">
          Win % by Sport
        </CardTitle>
        <PieChart className="h-4 w-4 text-zinc-400" />
      </div>
    </CardHeader>
    <CardContent className="p-3 pt-0">
      <div className="space-y-3">
        {sports.length > 0 ? (
          sports.map((sport) => (
            <div key={sport.name}>
              <div className="flex justify-between items-center mb-1">
                <div className="flex items-center gap-1">
                  <span className="text-sm truncate max-w-32">
                    {sport.name}
                  </span>
                  <span className="text-zinc-500 text-xs">({sport.count})</span>
                </div>
                <span
                  className={cn(
                    "text-xs font-medium",
                    sport.winRate >= 60
                      ? "text-green-500"
                      : sport.winRate <= 40
                        ? "text-red-500"
                        : "text-zinc-400",
                  )}
                >
                  {sport.winRate.toFixed(1)}%
                </span>
              </div>
              <div className="flex h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className={cn(
                    "h-full rounded-full",
                    sport.winRate >= 60
                      ? "bg-green-500"
                      : sport.winRate <= 40
                        ? "bg-red-500"
                        : "bg-blue-500",
                  )}
                  style={{ width: `${sport.winRate}%` }}
                />
              </div>
            </div>
          ))
        ) : (
          <div className="text-center text-zinc-500 text-xs py-4">
            No data available
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

const BettingStats: React.FC<BettingStatsProps> = ({
  bets,
  personas,
  statsData,
}) => {
  // Calculate statistics from passed bets
  const stats = useMemo(() => {
    const totalBets = bets.length;
    const pendingBets = bets.filter((bet) => bet.status === "pending").length;
    const wonBets = bets.filter((bet) => bet.status === "won").length;
    const lostBets = bets.filter((bet) => bet.status === "lost").length;
    const settledBets = wonBets + lostBets;
    const winPercentage = settledBets > 0 ? (wonBets / settledBets) * 100 : 0;

    return {
      total_bets: totalBets,
      pending_bets: pendingBets,
      won_bets: wonBets,
      lost_bets: lostBets,
      win_percentage: winPercentage,
    } satisfies Pick<
      BetStats,
      | "total_bets"
      | "pending_bets"
      | "won_bets"
      | "lost_bets"
      | "win_percentage"
    >;
  }, [bets]);

  // Calculate agent performance
  const agentPerformance = useMemo(() => {
    const agentMap = new Map();

    bets.forEach((bet) => {
      if (!agentMap.has(bet.persona_id)) {
        const persona = personas.find((p) => p.persona_id === bet.persona_id);
        agentMap.set(bet.persona_id, {
          persona_id: bet.persona_id,
          name: persona
            ? persona.name
            : `Agent ${bet.persona_id.substring(0, 6)}...`,
          wins: 0,
          losses: 0,
          total: 0,
        });
      }

      const agent = agentMap.get(bet.persona_id);
      agent.total++;

      if (bet.status === "won") agent.wins++;
      if (bet.status === "lost") agent.losses++;
    });

    return Array.from(agentMap.values())
      .map((agent) => ({
        ...agent,
        winPercentage:
          agent.wins + agent.losses > 0
            ? (agent.wins / (agent.wins + agent.losses)) * 100
            : 0,
        totalBets: agent.total,
      }))
      .sort((a, b) => b.winPercentage - a.winPercentage)
      .slice(0, 5);
  }, [bets, personas]);

  // Calculate sport performance
  const sportPerformance = useMemo(() => {
    const sportMap = new Map();

    bets.forEach((bet) => {
      const sport = bet.sport || "Unknown";

      if (!sportMap.has(sport)) {
        sportMap.set(sport, {
          name: sport,
          wins: 0,
          losses: 0,
          total: 0,
        });
      }

      const sportData = sportMap.get(sport);
      sportData.total++;

      if (bet.status === "won") sportData.wins++;
      if (bet.status === "lost") sportData.losses++;
    });

    return Array.from(sportMap.values())
      .map((sport) => ({
        ...sport,
        winRate:
          sport.wins + sport.losses > 0
            ? (sport.wins / (sport.wins + sport.losses)) * 100
            : 0,
        count: sport.total,
      }))
      .filter((sport) => sport.total >= 3) // Only include sports with at least 3 bets
      .sort((a, b) => b.winRate - a.winRate)
      .slice(0, 5);
  }, [bets]);

  // Use API stats data if available, otherwise use calculated stats
  const displayStats = statsData || stats;

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
      <div className="md:col-span-3">
        <StatCard
          title="Total Bets"
          value={displayStats.total_bets}
          description={`${displayStats.pending_bets} pending`}
          icon={<Calendar className="h-4 w-4 text-zinc-400" />}
        />
      </div>

      <div className="md:col-span-3">
        <WinRateCard
          wonBets={displayStats.won_bets}
          lostBets={displayStats.lost_bets}
          winPercentage={displayStats.win_percentage}
        />
      </div>

      <div className="md:col-span-3">
        <TopAgentsCard agents={agentPerformance} />
      </div>

      <div className="md:col-span-3">
        <SportsDistributionCard sports={sportPerformance} />
      </div>
    </div>
  );
};

export default BettingStats;
